import React from 'react'
import {
  Pen7676HeadlineWrapper,
  usePen7676FooterViewClasses,
} from '../../experiments/pen-7676_v1_promo_sticky_footer_face_stack'
import { useStickyFooterGiftCtaExperiment } from '../../experiments/pen-7694_v1_promo_sticky_footer_gift_cta_routing'
import { useGiftEnabled } from '../../lib/helpers'
import { GiftButton } from '../GiftButton'
import { UrgencyText } from '../featureVariables'
import { StickyFooterContentTitle } from './StickyFooterContentTitle'
import classes from './StickyFooterContent.module.scss'

const LOCATION = 'sticky_footer'

interface StickyFooterContentProps {
  SignUpCta: React.ComponentType<{ fullWidth?: boolean; className?: string }>
  PricingText: React.ComponentType
}

interface DesktopViewProps extends StickyFooterContentProps {
  showGiftButton?: boolean
  giftHref?: string
  className?: string
}

interface MobileViewProps extends StickyFooterContentProps {
  showGiftButton?: boolean
  giftHref?: string
  className?: string
}

const DesktopView = ({
  SignUpCta,
  PricingText,
  showGiftButton,
  giftHref,
  className = 'd-none d-sm-flex',
}: DesktopViewProps) => (
  <div className={`${className} align-items-center justify-content-between`}>
    <Pen7676HeadlineWrapper view='desktop'>
      <div className='d-flex flex-wrap align-items-center'>
        <StickyFooterContentTitle className='mc-mr-sm-4' />
        <p className='mc-text-small mc-text--capitalize'>
          <UrgencyText />
        </p>
      </div>
    </Pen7676HeadlineWrapper>
    <div className='d-flex align-items-center mc-ml-2'>
      <p className='mc-text-h7 mc-mr-2 mc-mr-md-3'>
        <PricingText />
      </p>
      {showGiftButton && (
        <div
          className={`d-flex flex-gap-2 align-items-end ${classes.invertedTertiary}`}
        >
          <GiftButton className='mc-mr-2' location={LOCATION} href={giftHref} />
        </div>
      )}
      <SignUpCta />
    </div>
  </div>
)

const MobileView = ({
  SignUpCta,
  PricingText,
  showGiftButton,
  giftHref,
  className = 'd-flex d-sm-none',
}: MobileViewProps) => (
  <div className={`${className} flex-column mc-gap-3`}>
    {showGiftButton ? (
      <>
        <div>
          <div className='mc-mb-2'>
            <Pen7676HeadlineWrapper view='mobile'>
              <StickyFooterContentTitle />
            </Pen7676HeadlineWrapper>
          </div>
          <p className='mc-font-caption'>
            <PricingText />
            {`. `}
            <span className='mc-text--capitalize'>
              <UrgencyText />
            </span>
          </p>
        </div>
        <div className='row'>
          <div className={`${classes.invertedTertiary} col-6`}>
            <GiftButton
              className='mc-mr-2 mc-py-2'
              location={LOCATION}
              href={giftHref}
              fullWidth
            />
          </div>
          <div className='col-6'>
            <SignUpCta fullWidth className='mc-py-2' />
          </div>
        </div>
      </>
    ) : (
      <>
        <div className='d-flex align-items-center justify-content-between mc-gap-4'>
          <StickyFooterContentTitle />
          <SignUpCta />
        </div>
        <div
          className={`d-flex justify-content-between mc-gap-4 ${classes.bottomContainer}`}
        >
          <p
            className='mc-font-caption align-self-end'
            style={{ minWidth: '33%' }}
          >
            <UrgencyText />
          </p>
          <p className='mc-font-caption mc-text--right'>
            <PricingText />
          </p>
        </div>
      </>
    )}
  </div>
)

export const StickyFooterContent = ({
  SignUpCta,
  PricingText,
}: StickyFooterContentProps) => {
  const { giftEnabled } = useGiftEnabled()
  const { giftHref } = useStickyFooterGiftCtaExperiment()
  const viewClasses = usePen7676FooterViewClasses()

  return (
    <div className='container mc-p-4'>
      <DesktopView
        SignUpCta={SignUpCta}
        PricingText={PricingText}
        showGiftButton={giftEnabled}
        giftHref={giftHref}
        className={viewClasses?.desktop}
      />
      <MobileView
        SignUpCta={SignUpCta}
        PricingText={PricingText}
        showGiftButton={giftEnabled}
        giftHref={giftHref}
        className={viewClasses?.mobile}
      />
    </div>
  )
}
